import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';
import moment from 'moment';
import './MergedContactsViewer.css';

/**
 * MergedContactsViewer Component
 * Displays the list of modified/merged contacts stored in the user's profile,
 * showing the exact date/time of each merge along with source details.
 */
export default function MergedContactsViewer({ mergedContacts = [] }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const contactsList = Array.isArray(mergedContacts) ? mergedContacts : [];

  // Filter contacts by search query
  const filteredList = contactsList.filter(item => {
    if (!searchTerm.trim()) return true;
    const query = searchTerm.toLowerCase();
    const displayNameMatch = (item.displayName || '').toLowerCase().includes(query);
    const notesMatch = (item.notes || '').toLowerCase().includes(query);
    const sourceMatch = (item.sourceContacts || []).some(src => {
      const nameMatch = (src.name || '').toLowerCase().includes(query);
      const companyMatch = (src.company || '').toLowerCase().includes(query);
      const emailMatch = (src.emails || []).some(e => String(e).toLowerCase().includes(query));
      const phoneMatch = (src.phones || []).some(p => String(p).toLowerCase().includes(query));
      return nameMatch || companyMatch || emailMatch || phoneMatch;
    });
    return displayNameMatch || notesMatch || sourceMatch;
  });

  // Format date/time with both absolute timestamp and relative time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return t('Unknown date');
    try {
      const m = moment(dateStr);
      if (!m.isValid()) return String(dateStr);
      return `${m.format('MMMM D, YYYY [at] h:mm:ss A')} (${m.fromNow()})`;
    } catch {
      return String(dateStr);
    }
  };

  return (
    <div className="merged-contacts-container">
      <div className="merged-contacts-header">
        <div className="merged-contacts-title-area">
          <h3>
            👥 {t('Merged & Deduplicated Contacts')}
            <span className="merged-contacts-count-badge">
              {contactsList.length} {t('Merges')}
            </span>
          </h3>
          <p>
            {t('History of contact merges performed during deduplication, recorded with exact date and time.')}
          </p>
        </div>

        {contactsList.length > 0 && (
          <div className="merged-contacts-filter-box">
            <Input
              type="text"
              placeholder={t('Search merged contacts...')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="form-control-sm"
            />
          </div>
        )}
      </div>

      {contactsList.length === 0 ? (
        <div className="merged-contacts-empty-state">
          <div className="empty-state-icon">📋</div>
          <h4>{t('No Merged Contacts Yet')}</h4>
          <p>
            {t(
              'When you merge duplicate contacts in the mobile app during Review Duplicate Contacts, the modified contact details and the exact date and time of each merge will be saved to your profile and displayed here.'
            )}
          </p>
        </div>
      ) : filteredList.length === 0 ? (
        <div className="merged-contacts-empty-state">
          <div className="empty-state-icon">🔍</div>
          <h4>{t('No Matching Merges Found')}</h4>
          <p>{t('Try searching with a different name, email, or phone number.')}</p>
        </div>
      ) : (
        <div className="merged-contacts-list">
          {filteredList.map((merge, index) => {
            const sourceA = merge.sourceContacts?.[0];
            const sourceB = merge.sourceContacts?.[1];

            return (
              <div key={merge.id || index} className="merged-contact-card">
                <div className="merged-contact-card-header">
                  <div className="merged-display-name">
                    <span>{merge.displayName || t('Merged Contact')}</span>
                    <span className="primary-badge">{t('Merged Display Name')}</span>
                  </div>
                  <div className="merged-timestamp-badge">
                    <span>📅</span>
                    <span>{formatDateTime(merge.mergedAt)}</span>
                  </div>
                </div>

                {merge.notes && (
                  <div className="merged-contact-notes">
                    {merge.notes}
                  </div>
                )}

                <div className="merged-sources-grid">
                  {/* Source 1 */}
                  <div className="source-contact-panel">
                    <div className="source-panel-header">
                      <span className="source-label">{t('Source Contact 1')}</span>
                      <span className={`source-type-pill ${sourceA?.sourceType === 'hmi_connection' ? 'hmi' : ''}`}>
                        {sourceA?.sourceType === 'hmi_connection'
                          ? t('HMI Connection')
                          : t('Device Contact')}
                      </span>
                    </div>
                    <div className="source-contact-name">
                      {sourceA?.name || t('Unknown')}
                    </div>
                    {sourceA?.company && (
                      <div className="source-field-row">
                        <span className="source-field-label">{t('Company')}:</span>
                        <span>{sourceA.company}</span>
                      </div>
                    )}
                    {sourceA?.phones && sourceA.phones.length > 0 && (
                      <div className="source-field-row">
                        <span className="source-field-label">{t('Phone')}:</span>
                        <span>{sourceA.phones.join(', ')}</span>
                      </div>
                    )}
                    {sourceA?.emails && sourceA.emails.length > 0 && (
                      <div className="source-field-row">
                        <span className="source-field-label">{t('Email')}:</span>
                        <span>{sourceA.emails.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {/* Divider / Arrow */}
                  <div className="merge-arrow-divider">
                    <div className="merge-icon-circle" title={t('Combined into single contact')}>
                      🔗
                    </div>
                  </div>

                  {/* Source 2 */}
                  <div className="source-contact-panel">
                    <div className="source-panel-header">
                      <span className="source-label">{t('Source Contact 2')}</span>
                      <span className={`source-type-pill ${sourceB?.sourceType === 'hmi_connection' ? 'hmi' : ''}`}>
                        {sourceB?.sourceType === 'hmi_connection'
                          ? t('HMI Connection')
                          : t('Device Contact')}
                      </span>
                    </div>
                    <div className="source-contact-name">
                      {sourceB?.name || t('Unknown')}
                    </div>
                    {sourceB?.company && (
                      <div className="source-field-row">
                        <span className="source-field-label">{t('Company')}:</span>
                        <span>{sourceB.company}</span>
                      </div>
                    )}
                    {sourceB?.phones && sourceB.phones.length > 0 && (
                      <div className="source-field-row">
                        <span className="source-field-label">{t('Phone')}:</span>
                        <span>{sourceB.phones.join(', ')}</span>
                      </div>
                    )}
                    {sourceB?.emails && sourceB.emails.length > 0 && (
                      <div className="source-field-row">
                        <span className="source-field-label">{t('Email')}:</span>
                        <span>{sourceB.emails.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
